import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/admin/AdminSidebar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Plus, Edit, Trash2, Plane, MapPin, GripVertical, Check, X, Save } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = window.location.origin || '';

const AdminAirportsPage = () => {
  const navigate = useNavigate();
  const [airports, setAirports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAirport, setEditingAirport] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [hasOrderChanged, setHasOrderChanged] = useState(false);
  
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    name_tr: '',
    city: '',
    city_tr: '',
    country: 'Turkey',
    lat: '',
    lng: '',
    timezone: 'Europe/Istanbul',
    is_active: true,
    display_order: 10
  });

  const getAuthHeader = () => {
    const token = localStorage.getItem('vipgez_admin_token') || localStorage.getItem('admin_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    const token = localStorage.getItem('vipgez_admin_token') || localStorage.getItem('admin_token');
    if (!token) {
      navigate('/login-goturkeytransfer');
      return;
    }
    fetchAirports();
  }, [navigate]);

  const fetchAirports = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/airports`, {
        headers: { ...getAuthHeader() }
      });
      const data = await response.json();

      if (data.success) {
        // Sort by display_order
        const sorted = (data.airports || []).sort((a, b) => (a.display_order || 999) - (b.display_order || 999));
        setAirports(sorted);
      }
    } catch (error) {
      console.error('Failed to fetch airports:', error);
      toast.error('Failed to load airports');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingAirport 
        ? `${API_BASE}/api/admin/airports/${editingAirport.sqlite_id}`
        : `${API_BASE}/api/admin/airports`;
      
      const method = editingAirport ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          lat: parseFloat(formData.lat) || 0,
          lng: parseFloat(formData.lng) || 0,
          display_order: parseInt(formData.display_order) || 10
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success(editingAirport ? 'Airport updated' : 'Airport created');
        setShowForm(false);
        setEditingAirport(null);
        resetForm();
        fetchAirports();
      } else {
        toast.error(data.detail || data.error || 'Operation failed');
      }
    } catch (error) {
      console.error('Airport save error:', error);
      toast.error('Failed to save airport');
    }
  };

  const handleEdit = (airport) => {
    setEditingAirport(airport);
    setFormData({
      code: airport.code || '',
      name: airport.name || '',
      name_tr: airport.name_tr || '',
      city: airport.city || '',
      city_tr: airport.city_tr || '',
      country: airport.country || 'Turkey',
      lat: airport.lat?.toString() || '',
      lng: airport.lng?.toString() || '',
      timezone: airport.timezone || 'Europe/Istanbul',
      is_active: airport.is_active ?? true,
      display_order: airport.display_order || 10
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this airport?')) return;

    try {
      const response = await fetch(`${API_BASE}/api/admin/airports/${id}`, {
        method: 'DELETE',
        headers: { ...getAuthHeader() }
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Airport deleted');
        fetchAirports();
      } else {
        toast.error('Failed to delete');
      }
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const handleToggle = async (id, currentStatus) => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/airports/${id}/toggle`, {
        method: 'POST',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !currentStatus })
      });

      const data = await response.json();
      if (data.success) {
        fetchAirports();
      }
    } catch (error) {
      toast.error('Toggle failed');
    }
  };

  // Drag & Drop handlers
  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
    e.target.classList.add('opacity-50');
  };

  const handleDragEnd = (e) => {
    e.target.classList.remove('opacity-50');
    setDraggedItem(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedItem === null || draggedItem === dropIndex) return;
    
    const newAirports = [...airports];
    const [draggedAirport] = newAirports.splice(draggedItem, 1);
    newAirports.splice(dropIndex, 0, draggedAirport);
    
    // Update display_order
    const updatedAirports = newAirports.map((airport, idx) => ({
      ...airport,
      display_order: idx + 1
    }));
    
    setAirports(updatedAirports);
    setHasOrderChanged(true);
  };

  const saveOrder = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/airports/reorder`, {
        method: 'POST',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          orders: airports.map((a, idx) => ({
            id: a.sqlite_id,
            display_order: idx + 1
          }))
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Order saved');
        setHasOrderChanged(false);
      } else {
        toast.error('Failed to save order');
      }
    } catch (error) {
      toast.error('Save order failed');
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      name_tr: '',
      city: '',
      city_tr: '',
      country: 'Turkey',
      lat: '',
      lng: '',
      timezone: 'Europe/Istanbul',
      is_active: true,
      display_order: airports.length + 1
    });
  };

  return (
    <div className="flex min-h-screen bg-background admin-theme" data-admin-panel="true">
      <AdminSidebar />
      
      <main className="flex-1 p-6 lg:p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-foreground" data-testid="airports-title">Airports</h1>
              <p className="text-muted-foreground">Drag & drop to reorder airports</p>
            </div>
            <div className="flex gap-2">
              {hasOrderChanged && (
                <Button onClick={saveOrder} variant="default" data-testid="save-order-btn">
                  <Save className="h-4 w-4 mr-2" />
                  Save Order
                </Button>
              )}
              <Button onClick={() => { setShowForm(true); setEditingAirport(null); resetForm(); }} data-testid="add-airport-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Airport
              </Button>
            </div>
          </div>

          {/* Form */}
          {showForm && (
            <Card className="bg-secondary border-border mb-6">
              <CardHeader>
                <CardTitle className="text-foreground">{editingAirport ? 'Edit Airport' : 'New Airport'}</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm font-medium text-foreground">Airport Code (IATA)</label>
                      <Input
                        name="code"
                        value={formData.code}
                        onChange={handleInputChange}
                        placeholder="IST"
                        maxLength={3}
                        required
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-foreground">Name (English)</label>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Istanbul Airport"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-foreground">Name (Turkish)</label>
                      <Input
                        name="name_tr"
                        value={formData.name_tr}
                        onChange={handleInputChange}
                        placeholder="İstanbul Havalimanı"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm font-medium text-foreground">City (English)</label>
                      <Input
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder="Istanbul"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-foreground">City (Turkish)</label>
                      <Input
                        name="city_tr"
                        value={formData.city_tr}
                        onChange={handleInputChange}
                        placeholder="İstanbul"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-foreground">Country</label>
                      <Input
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        placeholder="Turkey"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm font-medium text-foreground">Latitude</label>
                      <Input
                        name="lat"
                        type="number"
                        step="0.000001"
                        value={formData.lat}
                        onChange={handleInputChange}
                        placeholder="41.2753"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-foreground">Longitude</label>
                      <Input
                        name="lng"
                        type="number"
                        step="0.000001"
                        value={formData.lng}
                        onChange={handleInputChange}
                        placeholder="28.7519"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-foreground">Timezone</label>
                      <Input
                        name="timezone"
                        value={formData.timezone}
                        onChange={handleInputChange}
                        placeholder="Europe/Istanbul"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                      className="rounded border-border"
                    />
                    <span className="text-sm text-foreground">Active (show in booking form)</span>
                  </div>

                  <div className="flex gap-3">
                    <Button type="submit">{editingAirport ? 'Update' : 'Create'}</Button>
                    <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditingAirport(null); }}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Airports List with Drag & Drop */}
          <Card className="bg-secondary border-border">
            <CardHeader>
              <CardTitle className="text-foreground">Airports ({airports.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full"></div>
                </div>
              ) : airports.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  No airports found
                </div>
              ) : (
                <div className="space-y-2">
                  {airports.map((airport, idx) => (
                    <div
                      key={airport.sqlite_id || airport.code}
                      draggable
                      onDragStart={(e) => handleDragStart(e, idx)}
                      onDragEnd={handleDragEnd}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, idx)}
                      className={`flex items-center gap-4 p-4 border border-border rounded-lg hover:bg-muted/30 transition-colors cursor-move ${
                        !airport.is_active ? 'opacity-50' : ''
                      }`}
                      data-testid={`airport-${airport.code}`}
                    >
                      {/* Drag Handle */}
                      <div className="text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing">
                        <GripVertical className="h-5 w-5" />
                      </div>

                      {/* Order Number */}
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                        {idx + 1}
                      </div>

                      {/* Airport Info */}
                      <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="flex items-center gap-2">
                          <Plane className="h-4 w-4 text-primary" />
                          <span className="font-mono font-bold text-foreground">{airport.code}</span>
                        </div>
                        <div>
                          <p className="font-medium text-foreground">{airport.name}</p>
                          {airport.name_tr && (
                            <p className="text-xs text-muted-foreground">{airport.name_tr}</p>
                          )}
                        </div>
                        <div>
                          <p className="text-foreground">{airport.city}</p>
                          <p className="text-xs text-muted-foreground">{airport.country}</p>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <MapPin className="h-3 w-3" />
                          <span>{airport.lat?.toFixed(4)}, {airport.lng?.toFixed(4)}</span>
                        </div>
                      </div>

                      {/* Status & Actions */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleToggle(airport.sqlite_id, airport.is_active)}
                          className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            airport.is_active 
                              ? 'bg-green-500/10 text-green-500' 
                              : 'bg-gray-500/10 text-gray-500'
                          }`}
                        >
                          {airport.is_active ? 'Active' : 'Inactive'}
                        </button>
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(airport)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(airport.sqlite_id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AdminAirportsPage;
